/* eslint-disable react/prefer-stateless-function */
/* eslint-disable react/static-property-placement */

import mitt from 'mitt'
import type { MouseEvent, ReactNode } from 'react'

import { bootIntercom } from '{src}/javascripts/sharedWithConsole/intercom-facade/boot-intercom.ts'

// Proxy Intercom events through a singleton event emitter because the intercom API doesn't support removeEventListener (we would risk memory leak)
const intercomEmitter = mitt()

// This module needs a lot of custom logic because the Intercom API is quite lacking:
// https://developers.intercom.com/installing-intercom/docs/intercom-javascript

// We need to track the state of intercom here, because there is no API for getting its shown status
let isShown = false

const attachIntercomListeners = () => {
  if (window.Intercom) {
    // There is a way to know if Intercom Javascript has been blocked. You can use Intercom('onUnreadCountChange') as part of the JavaScript API.
    // It will trigger for pages that have Intercom loaded.
    // https://forum.intercom.com/s/question/0D52G00004WxWMg/how-can-i-check-if-an-adblocker-has-prevented-the-messenger-script-from-loading

    window.Intercom('onUnreadCountChange', () => {
      intercomEmitter.emit('load')
    })

    window.Intercom('onShow', () => {
      isShown = true
      intercomEmitter.emit('show')
    })

    window.Intercom('onHide', () => {
      isShown = false
      intercomEmitter.emit('show')
    })
  }
}

attachIntercomListeners()

async function tryShowIntercom(message: string) {
  const intercomShowTimeout = 3000

  function intercomShow() {
    window.Intercom('showNewMessage', message ? `${message}: ` : undefined)
  }

  if (isShown) {
    return intercomShow()
  }

  return new Promise<void>((resolve, reject) => {
    function checkIntercomFrame() {
      const intercomFrame = document.querySelector('iframe[name^="intercom-messenger"]')
      if (intercomFrame) {
        cleanup()
        resolve()
      }
    }

    function cleanup() {
      clearTimeout(timer)
      clearInterval(checkInterval)
    }

    const timer = setTimeout(() => {
      cleanup()
      reject(new Error('Timed out waiting for Intercom to show'))
    }, intercomShowTimeout)

    const checkInterval = setInterval(checkIntercomFrame, 100) // Check every 100ms

    intercomShow()
  })
}

interface Props {
  className?: string
  message?: string
  children: ReactNode
}

// See https://github.com/transloadit/content/issues/1557
const IntercomLink = ({ children, className, message = 'Question' }: Props) => {
  const fallback = new URL('mailto:support@transloadit.com')
  if (message) {
    fallback.searchParams.set('subject', encodeURIComponent(message))
  }
  const fallbackHref = fallback.toString()

  async function contactSupport(event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault()

    try {
      if (!window.Intercom) {
        await bootIntercom()
        attachIntercomListeners()
      }
      await tryShowIntercom(message)
    } catch (tryShowIntercomError) {
      console.error(tryShowIntercomError)
      // it's too late to change the link now, as the href of this event is already "#", so we will redirect instead
      window.location.href = fallbackHref
    }
  }

  // set target to __blank if not handled by intercom, or else user may get a confirm navigate away message
  // https://stackoverflow.com/questions/9740510/mailto-link-in-chrome-is-triggering-window-onbeforeunload-can-i-prevent-this
  return (
    <a
      className={className}
      href={fallbackHref}
      target="_blank"
      rel="noopener noreferrer"
      onClick={contactSupport}
    >
      {children}
    </a>
  )
}

export default IntercomLink
